<template>
  <div class="tags-container">
    <a-tag
        v-for="(tab) in tabs"
        :key="tab.key"
        :color="activeKey === tab.key ? color : 'gray'"
        @click="handleTabChange(tab.key)"
        @close="handleTabClose(tab.key)"
        closable
    >
      {{ tab.title }}
    </a-tag>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useTabStore } from '@/store/tabs'
import { useRouter, useRoute } from 'vue-router'
import { useRouteCache } from '@/utils/useRouteCache'
import { storeToRefs } from 'pinia'

const route = useRoute()
const router = useRouter()
const tabStore = useTabStore()
const { tabs, activeKey } = storeToRefs(tabStore)
const { refreshCache } = useRouteCache()
const color = ref('#0960bd')

// 最大标签页数量限制
const MAX_TAB_COUNT = 8

const selectedKeys = ref([route.path])

// 处理带数量限制的tab添加逻辑
const handleAddTabWithLimit = (path, title) => {
  // 1. 如果新tab已存在，直接添加（避免重复）
  const tabExists = tabs.value.some(tab => tab.key === path)
  if (tabExists) {
    tabStore.addTab(path, title)
    return
  }

  // 2. 如果达到数量上限，先删除最旧的非激活tab
  if (tabs.value.length >= MAX_TAB_COUNT) {
    // 过滤出需要保留的tab（当前激活的 + 即将添加的新tab）
    const keepTabKeys = [activeKey.value, path]
    // 找到可以删除的tab（排除需要保留的），取最旧的第一个
    const deletableTabs = tabs.value.filter(tab => !keepTabKeys.includes(tab.key))

    if (deletableTabs.length > 0) {
      const oldestTabKey = deletableTabs[0].key
      // 删除最旧的tab并清理缓存
      tabStore.removeTab(oldestTabKey)
      refreshCache(oldestTabKey)
    }
  }

  // 3. 添加新tab
  tabStore.addTab(path, title)
}

// 路由变化处理
watch(
    () => route.path,
    (newPath) => {
      selectedKeys.value = [newPath]
      // 替换原有的直接添加逻辑，使用带限制的添加方法
      handleAddTabWithLimit(newPath, route.meta?.title || '未命名')
    },
    { immediate: true }
)

const handleTabClose = (key) => {
  // 删除标签页
  tabStore.removeTab(key)
  // 销毁该路由的缓存
  refreshCache(key)
  // 跳转到当前激活标签页
  router.push(tabStore.activeKey)
}

const handleTabChange = (key) => {
  tabStore.activeKey = key
  router.push(key)
}
</script>

<style scoped>
.tags-container {
  display: flex;
  gap: 8px;
  padding: 8px;
  background: #fff;
  overflow-x: auto;
  scrollbar-width: none;
}

.tags-container::-webkit-scrollbar {
  display: none;
}

.tab-tag {
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
  height: 28px;
  line-height: 26px;
  font-size: 12px;
  padding: 0 10px;
  display: flex;
  align-items: center;
}

.tab-tag:hover {
  opacity: 0.9;
}

.tab-tag :deep(.el-tag__close) {
  color: inherit;
  margin-left: 4px;
}

.tab-tag :deep(.el-tag__close:hover) {
  background-color: rgba(255, 255, 255, 0.3);
}
</style>
